import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Header, Button, Divider, Icon, Image, Popup, Segment, Form as SemanticForm } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import { doc, onSnapshot } from "firebase/firestore";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { db } from "../../../../app/config/firebase";
import { stripePromise } from "../../../../app/config/stripe";
import {
  functionUnlockStrategyProperty,
  functionGetStrategyCreditProducts,
  functionCreateStrategyCreditCheckout,
  functionGetStrategyEntitlement,
} from "../../../../app/firestore/functionsService";
import { updateTransactionSettingsInDb } from "../../../../app/firestore/firestoreService";
import { ensureHttpsImageUrl } from "../../../../app/common/util/util";
import { formatPrice } from "../../../../app/common/util/formatPrice";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { isStrategyVisible } from "./strategyVisibility";
import FormAddress from "../../../../app/common/form/FormAddress";

// How long to keep watching the balance doc for the webhook-driven update
// before giving up and just telling the buyer to check back - mirrors
// TransactionStrategyDashboard.jsx's identical CREDIT_FULFILLMENT_WAIT_MS.
const CREDIT_FULFILLMENT_WAIT_MS = 45000;

export const NAVY = "#16243c";
export const BLUE = "#2185d0";
export const LIGHT_BLUE_BG = "#eaf4fc";
export const LIGHT_BLUE_BORDER = "#cfe4f7";
export const GREY_TEXT = "#767676";

// Stored `value` (not the displayed `text`) is what actually reaches
// functions/index.js's categorizePropertyType() as data.propertyType (the
// SAME field the "Edit Details" form's free-text Property Type input feeds -
// this dropdown's real job is making sure that field gets filled in
// correctly up front). categorizePropertyType keyword-matches the raw
// string (case-insensitive): "single family|detached" for SFH, "town
// ?house|town ?home" for Townhome, "condo|co-?op" for Condo, "land|lot|
// vacant" for Land, "commercial|industrial|farm" for Commercial - a plain
// "SFH" abbreviation would NOT match "single family", so the SFH option's
// value is spelled out even though its label stays short. Getting this
// category right is what lets a comp be scored propertyTypeMatch: "direct"
// instead of "context_only" (never upgradeable to "Strong" comp strength) -
// this is the actual comp-matching this field exists to improve.
const PROPERTY_TYPE_OPTIONS = [
  { key: "sfh", text: "SFH", value: "Single Family" },
  { key: "townhome", text: "Townhome", value: "Townhome" },
  { key: "condo", text: "Condo", value: "Condo" },
  { key: "land", text: "Land", value: "Land" },
  { key: "commercial", text: "Commercial", value: "Commercial" },
];

// Value is capped at 4 ("4+") since bedroom count only ever feeds the
// prompt as informational context (functions/index.js's "Total bedrooms: X"
// line) - unlike square footage, it never drives the deterministic comp-sqft
// comparison, so a precise count above 4 isn't needed.
const BEDROOM_OPTIONS = [
  { key: "0", text: "0", value: 0 },
  { key: "1", text: "1", value: 1 },
  { key: "2", text: "2", value: 2 },
  { key: "3", text: "3", value: 3 },
  { key: "4", text: "4+", value: 4 },
];

// Value is the exact display string - passed straight through to the
// Strategy prompt as free text ("Condition (agent-provided): X"), so there's
// no separate code-to-label mapping to keep in sync.
const CONDITION_OPTIONS = [
  { key: "new_or_updated", text: "New or Updated", value: "New or Updated" },
  { key: "average", text: "Average", value: "Average" },
  { key: "dated", text: "Dated", value: "Dated" },
  { key: "needs_work", text: "Needs Work", value: "Needs Work" },
];

// Filtered by the central Strategy visibility config (strategyVisibility.js),
// so a hidden Strategy is never advertised here. "And More" always shows.
const ALL_STRATEGY_FEATURE_PILLS = [
  { key: "propertyIntelligence", icon: "search", label: "Property Intelligence" },
  { key: "listingDescription", icon: "pencil alternate", label: "Listing Description" },
  { key: "pricing", icon: "chart bar", label: "Pricing" },
  { key: "negotiation", icon: "handshake outline", label: "Negotiation" },
  { key: "marketing", icon: "bullhorn", label: "Marketing" },
  { key: "inspection", icon: "search", label: "Inspection" },
  { key: "offer", icon: "file alternate outline", label: "Offer" },
];
const STRATEGY_FEATURE_PILLS = [
  ...ALL_STRATEGY_FEATURE_PILLS.filter((pill) => isStrategyVisible(pill.key)),
  { icon: "magic", label: "And More" },
];
const INCLUDED_STRATEGIES_TEXT = `Includes ${ALL_STRATEGY_FEATURE_PILLS.filter((pill) => isStrategyVisible(pill.key)).map((pill) => pill.label).join(", ")} and future available Strategies.`;

// Mirrors buildPropertyKey's own requirement (functions/strategyCredits/propertyKey.js)
// - street + city + state is the minimum an unlock can actually key off of.
export function hasCompleteAddress(address) {
  return !!(address?.street && address?.city && address?.state);
}

export function TransActionerProLogo() {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
      <Image src="/assets/TA-Logo-Pro.png" alt="TransActioner Pro" style={{ maxHeight: 80, width: "auto" }} />
    </div>
  );
}

function StrategyFeaturePills() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
      {STRATEGY_FEATURE_PILLS.map((pill) => (
        <div
          key={pill.label}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: LIGHT_BLUE_BG,
            color: NAVY,
            borderRadius: 999,
            padding: "6px 14px",
            fontWeight: 600,
            fontSize: "0.85em",
          }}
        >
          <Icon name={pill.icon} color="blue" style={{ margin: 0 }} />
          {pill.label}
        </div>
      ))}
    </div>
  );
}

// Shown in place of the address display when the transaction doesn't have
// one yet (e.g. a Buyer transaction before an offer address is set) - lets
// the user provide it right here instead of dead-ending the Unlock flow.
// Saves directly to the transaction doc; the unlock callable always reads
// the transaction's CURRENT address server-side, so no other plumbing is
// needed once this resolves.
export function MissingAddressForm({ transactionId, initialAddress, onSaved }) {
  const initialValues = {
    address: {
      street: initialAddress?.street || "",
      unit: initialAddress?.unit || "",
      city: initialAddress?.city || "",
      state: initialAddress?.state || "",
      zipcode: initialAddress?.zipcode || "",
    },
  };

  const validationSchema = Yup.object({
    address: Yup.object({
      street: Yup.string().required("Street address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
    }),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await updateTransactionSettingsInDb(transactionId, { address: values.address });
          toast.success("Address saved.");
          onSaved(values.address);
        } catch (error) {
          toast.error(error?.message || "Unable to save the address.");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, isValid }) => (
        <Form className="ui form">
          <FormAddress />
          <Button
            primary
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting || !isValid}
            content="Save Address"
            floated="right"
            className="small top margin"
          />
          <div className="clear" />
        </Form>
      )}
    </Formik>
  );
}

// A single selectable funding-source tile (Free/Comped, Paid, Manager-
// Sponsored). Always rendered even at 0/unavailable (greyed out) rather than
// conditionally hidden, so the picker's layout is stable and communicates
// the manager-pool option exists even before the user has ever used it.
export function FundingSourceCard({ icon, title, count, description, selected, disabled, onSelect }) {
  return (
    <div
      onClick={disabled ? undefined : onSelect}
      style={{
        flex: "1 1 0",
        minWidth: 150,
        border: `2px solid ${selected ? BLUE : "#e3e6ea"}`,
        background: selected ? LIGHT_BLUE_BG : "#fff",
        borderRadius: 10,
        padding: "14px 16px",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "default" : "pointer",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: `2px solid ${selected ? BLUE : "#c7ccd1"}`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {selected && <span style={{ width: 9, height: 9, borderRadius: "50%", background: BLUE }} />}
        </span>
        <Icon name={icon} size="large" color={selected ? "blue" : "grey"} style={{ margin: 0 }} />
      </div>
      <div style={{ fontWeight: 700, color: NAVY }}>{title}</div>
      <div style={{ fontWeight: 700, color: disabled ? "#9aa1a8" : BLUE, marginTop: 2 }}>{count} available</div>
      <div style={{ color: GREY_TEXT, fontSize: "0.85em", marginTop: 4 }}>{description}</div>
    </div>
  );
}

// Buy Credits section (Phase 1 Stripe integration): the server is the only
// source of truth for which products exist, their price, and how many
// credits they grant (functions/strategyCredits/payments/) - this component
// never hardcodes a price or credit count. Clicking Buy mounts Stripe
// Embedded Checkout right here in the modal (an iframe, via the session's
// client_secret) instead of redirecting the whole browser to Stripe-hosted
// Checkout - credits are still granted only once Stripe's webhook confirms
// payment, never from the client-side onComplete callback below, but staying
// on this page means there's no hard navigation to survive and no modal/
// transaction state to lose and recover afterward.
export function BuyStrategyCreditsSection({ isMobile, transactionId, onCreditsFulfilled }) {
  const uid = useSelector((state) => state.auth.currentUser?.uid);
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [buyingProductKey, setBuyingProductKey] = useState(null);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [creditPurchaseStatus, setCreditPurchaseStatus] = useState(null); // "pending" | "fulfilled" | "timeout" | null
  // Shared by the fetch effect below AND handleBuy's catch block - both set
  // state after an await, and closing the modal (or clicking Buy right
  // before it closes) can unmount this component before either resolves.
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    functionGetStrategyCreditProducts()
      .then((data) => {
        if (isMountedRef.current) setProducts(data?.products || []);
      })
      .catch((error) => {
        // Surfaced instead of silently rendering nothing - a missing/
        // misconfigured STRIPE_SECRET_KEY secret or an un-deployed function
        // throws here, and disappearing silently made that undiagnosable.
        if (isMountedRef.current) setLoadError(error?.message || "Unable to load Strategy Credit purchase options.");
      });
  }, []);

  async function handleBuy(productKey) {
    setBuyingProductKey(productKey);
    try {
      const { clientSecret } = await functionCreateStrategyCreditCheckout({ productKey });
      if (isMountedRef.current) setCheckoutClientSecret(clientSecret);
    } catch (error) {
      toast.error(error?.message || "Unable to start checkout right now.");
    } finally {
      if (isMountedRef.current) setBuyingProductKey(null);
    }
  }

  // Fires once Stripe confirms the payment client-side - NOT the same as the
  // credit actually landing, which only Stripe's webhook can do server-side
  // (functions/strategyCredits/payments/webhook.js). This just starts
  // watching this user's own strategyCreditBalances doc (readable directly
  // per firebase.rules) for that webhook's write, then re-fetches the
  // entitlement so the parent modal can reflect the new balance.
  function handleCheckoutComplete() {
    setCheckoutClientSecret(null);
    setCreditPurchaseStatus("pending");
    let settled = false;

    const unsubscribe = onSnapshot(
      doc(db, "strategyCreditBalances", `user_${uid}`),
      (snap) => {
        const data = snap.data();
        const updatedAtMillis = data?.updatedAt?.toMillis ? data.updatedAt.toMillis() : null;
        const isRecentUpdate = updatedAtMillis && Date.now() - updatedAtMillis < 3 * 60 * 1000;
        if (settled || !isRecentUpdate) return;

        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        functionGetStrategyEntitlement({ transactionId })
          .then((entitlementData) => {
            if (!isMountedRef.current) return;
            setCreditPurchaseStatus("fulfilled");
            if (onCreditsFulfilled) onCreditsFulfilled(entitlementData);
          })
          .catch(() => {
            if (isMountedRef.current) setCreditPurchaseStatus("fulfilled");
          });
      },
      () => {
        settled = true;
        if (isMountedRef.current) setCreditPurchaseStatus("timeout");
      }
    );

    const timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      if (isMountedRef.current) setCreditPurchaseStatus("timeout");
    }, CREDIT_FULFILLMENT_WAIT_MS);
  }

  function retryEntitlementCheck() {
    setCreditPurchaseStatus("pending");
    functionGetStrategyEntitlement({ transactionId })
      .then((entitlementData) => {
        if (!isMountedRef.current) return;
        setCreditPurchaseStatus("fulfilled");
        if (onCreditsFulfilled) onCreditsFulfilled(entitlementData);
      })
      .catch(() => {
        if (isMountedRef.current) setCreditPurchaseStatus("timeout");
      });
  }

  if (checkoutClientSecret) {
    return (
      <div>
        <Button
          basic
          size="tiny"
          icon="arrow left"
          content="Back"
          onClick={() => setCheckoutClientSecret(null)}
          style={{ marginBottom: 10 }}
        />
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret: checkoutClientSecret, onComplete: handleCheckoutComplete }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    );
  }

  if (creditPurchaseStatus === "pending") {
    return (
      <Segment secondary>
        <Icon name="circle notched" loading /> Confirming your payment and updating your balance&hellip;
      </Segment>
    );
  }
  if (creditPurchaseStatus === "fulfilled") {
    return (
      <Segment secondary color="green">
        <Icon name="check circle" color="green" /> Strategy Credit added - select it above to continue.
      </Segment>
    );
  }
  if (creditPurchaseStatus === "timeout") {
    return (
      <Segment secondary color="yellow">
        <Icon name="clock outline" /> Your payment was received but is taking longer than usual to reflect here.{" "}
        <Button basic size="small" onClick={retryEntitlementCheck}>
          Check again
        </Button>
      </Segment>
    );
  }

  if (loadError) {
    console.log("Strategy Credit load Error: ", loadError);
    return (
      <p className="text-medium text-grey">
        Strategy Credit purchases aren't available right now.
      </p>
    );
  }
  if (products === null) {
    return <p className="text-medium text-grey">Loading purchase options&hellip;</p>;
  }
  if (products.length === 0) {
    return <p className="text-medium text-grey">No Strategy Credit purchase options are configured right now.</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {products.map((product) => (
        <div
          key={product.productKey}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 14,
            background: LIGHT_BLUE_BG,
            border: `1px solid ${LIGHT_BLUE_BORDER}`,
            borderRadius: 10,
            padding: "14px 18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Icon name="shopping cart" size="big" color="blue" style={{ margin: 0 }} />
            <div>
              <div style={{ fontWeight: 700, color: BLUE }}>Need another Strategy Credit?</div>
              <div style={{ fontWeight: 700, color: NAVY, marginTop: 2 }}>
                {product.label} &mdash; {formatPrice(product.amount, product.currency)}
              </div>
              <div style={{ color: GREY_TEXT, fontSize: "0.8em", marginTop: 2, maxWidth: 420 }}>
                Unlock AI-powered Strategies for one property within a transaction. Regenerations and edits for that property are included.
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", width: isMobile ? "100%" : undefined }}>
            <Button
              primary
              fluid={isMobile}
              loading={buyingProductKey === product.productKey}
              disabled={!!buyingProductKey}
              onClick={() => handleBuy(product.productKey)}
              content="Buy Credit"
            />
            <div style={{ color: "#8a92a0", fontSize: "0.75em", marginTop: 4 }}>TransActioner Pro Strategy Credit</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Default/preselected funding-source order per spec section 48: Free/Comped
// first, Manager-sponsored next, Paid last - preserving personally-purchased
// credits where possible. Shown as radio options so the user can override.
export function defaultFundingSource(entitlement) {
  if (entitlement.personalCredits.freeComped > 0) return "free_comped";
  if (entitlement.managerSponsoredCredits?.available) return "manager_pool";
  if (entitlement.personalCredits.paid > 0) return "paid";
  return null;
}

export default function StrategyCreditConfirmModal({
  transactionId,
  propertyAddress,
  propertyPic,
  propertyDetails,
  triggerStrategyType,
  entitlement,
  onConfirmed,
}) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  // Overridden once a Buy Credit purchase inside this modal is fulfilled
  // (see BuyStrategyCreditsSection's onCreditsFulfilled) - the entitlement
  // prop itself was passed in when the modal opened and never changes.
  const [entitlementOverride, setEntitlementOverride] = useState(null);
  const activeEntitlement = entitlementOverride || entitlement;
  const [fundingSourceType, setFundingSourceType] = useState(defaultFundingSource(entitlement));
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [localPropertyAddress, setLocalPropertyAddress] = useState(propertyAddress);
  // Auto-populated from the transaction's existing MLS-linked propertyDetails
  // when available (same fields StrategyPricingModal's generation pipeline
  // already reads - functions/index.js's subjectAboveGrade/mlsPropertyDetails),
  // otherwise left for the user to fill in here so the eventual Strategy
  // generation has accurate structural facts for comp-matching from the start
  // rather than needing a separate research/edit pass first.
  const [propertyType, setPropertyType] = useState(propertyDetails?.propertyType || "");
  const [bedrooms, setBedrooms] = useState(
    Number.isFinite(propertyDetails?.bedroomsTotal) ? propertyDetails.bedroomsTotal : null
  );
  const [squareFeet, setSquareFeet] = useState(
    Number.isFinite(propertyDetails?.aboveGradeFinishedSqft) ? String(propertyDetails.aboveGradeFinishedSqft) : ""
  );
  const [condition, setCondition] = useState(propertyDetails?.condition || "");
  const [additionalNotes, setAdditionalNotes] = useState(propertyDetails?.additionalNotes || "");

  const { freeComped, paid } = activeEntitlement.personalCredits;
  const manager = activeEntitlement.managerSponsoredCredits;
  const addressReady = hasCompleteAddress(localPropertyAddress);

  // Auto-select the freshly-purchased Paid credit so the user doesn't have
  // to click it themselves right after paying.
  function handleCreditsFulfilled(entitlementData) {
    setEntitlementOverride(entitlementData);
    setFundingSourceType("paid");
  }

  function handleAddressSaved(newAddress) {
    setLocalPropertyAddress(newAddress);
  }

  function handleClose() {
    dispatch(closeModal({ modalType: "StrategyCreditConfirmModal" }));
  }

  // Same shape as transaction.propertyDetails itself, containing only the
  // fields the user actually provided here.
  function buildPropertyDetailsPatch() {
    const patch = {};
    if (propertyType) patch.propertyType = propertyType;
    if (Number.isFinite(bedrooms)) patch.bedroomsTotal = bedrooms;
    const parsedSqft = parseInt(String(squareFeet).replace(/[^0-9]/g, ""), 10);
    if (Number.isFinite(parsedSqft) && parsedSqft > 0) patch.aboveGradeFinishedSqft = parsedSqft;
    if (condition) patch.condition = condition;
    if (additionalNotes.trim()) patch.additionalNotes = additionalNotes.trim();
    return patch;
  }

  // Dot-notation keys so this only touches these nested fields - a plain
  // {propertyDetails: {...}} value would REPLACE the whole propertyDetails
  // map via updateDoc, wiping out any existing MLS-linked data (county,
  // yearBuilt, etc.) that this modal never even displays.
  async function savePropertyDetailsPatch(patch) {
    if (Object.keys(patch).length === 0) return;
    const updates = {};
    for (const [key, value] of Object.entries(patch)) {
      updates[`propertyDetails.${key}`] = value;
    }
    try {
      await updateTransactionSettingsInDb(transactionId, updates);
    } catch (error) {
      // Non-critical - these fields only help comp accuracy later; never
      // block the credit-consuming unlock action over this failing.
      console.error("Unable to save property details:", error);
    }
  }

  async function handleConfirm() {
    if (!fundingSourceType) return;
    setIsUnlocking(true);
    try {
      const propertyDetailsPatch = buildPropertyDetailsPatch();
      await savePropertyDetailsPatch(propertyDetailsPatch);
      await functionUnlockStrategyProperty({ transactionId, fundingSourceType, triggerStrategyType });
      handleClose();
      // Passed straight to StrategyPricingModal (via TransactionStrategyDashboard's
      // openPricingModal) rather than relying on it re-reading transaction.propertyDetails
      // from Redux - that write just happened and the live listener updating
      // props here can lag behind this synchronous handoff, which would show
      // the "next screen" with these fields still blank.
      if (onConfirmed) onConfirmed(propertyDetailsPatch);
      // No finally/setIsUnlocking(false) here - handleClose() just unmounted
      // this component (via closeModal), so there's nothing left to reset.
    } catch (error) {
      toast.error(error?.message || "Unable to unlock Strategies for this property right now.");
      setIsUnlocking(false);
    }
  }

  return (
    <ModalWrapper size="small">
      <Segment style={{ padding: isMobile ? "1.25rem" : "1.75rem 2rem 2rem" }}>
        <TransActionerProLogo />

        <Header
          as="h1"
          style={{ color: NAVY, fontWeight: 800, fontSize: isMobile ? "1.5em" : "1.9em", textAlign: "center", margin: "0 0 4px" }}
        >
          Unlock Intelligent Strategies
        </Header>

        {addressReady && (
          <>
            <p style={{ color: NAVY, fontWeight: 700, textAlign: "center", fontSize: "1.02em", margin: "0 0 2px" }}>
              1 Strategy Credit unlocks all AI-powered Strategies for this property.
            </p>
            <p style={{ color: GREY_TEXT, textAlign: "center", fontSize: "0.85em", margin: "0 0 16px" }}>
              {INCLUDED_STRATEGIES_TEXT} Your saved results and edits for this property are included.
            </p>

            <StrategyFeaturePills />
            <Divider hidden />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                background: "#f4f7fa",
                border: "1px solid #e7ebf0",
                borderRadius: 10,
                padding: 12,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Image
                src={propertyPic ? ensureHttpsImageUrl(propertyPic) : "/assets/placeholder-house.png"}
                style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: "1.05em" }}>
                  {localPropertyAddress?.street}
                  {localPropertyAddress?.unit ? ` ${localPropertyAddress.unit}` : ""}
                </div>
                <div style={{ color: "#5a6472" }}>
                  {localPropertyAddress?.city}, {localPropertyAddress?.state} {localPropertyAddress?.zipcode || ""}
                </div>
              </div>
              <Icon
                name="map marker alternate"
                color="blue"
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", opacity: 0.15, fontSize: "2.4em", margin: 0 }}
              />
            </div>

            <p style={{ color: GREY_TEXT, fontSize: "0.8em", margin: "14px 0 4px" }}>
              Fill in what you know - helps produce more accurate comps.
            </p>
            <SemanticForm>
              <SemanticForm.Group>
                <SemanticForm.Select
                  width={5}
                  label="Property Type"
                  placeholder="Select..."
                  options={PROPERTY_TYPE_OPTIONS}
                  value={propertyType}
                  onChange={(e, { value }) => setPropertyType(value)}
                />
                <SemanticForm.Select
                  width={3}
                  label="Bedrooms"
                  placeholder="Select..."
                  options={BEDROOM_OPTIONS}
                  value={bedrooms}
                  onChange={(e, { value }) => setBedrooms(value)}
                />
                <SemanticForm.Input
                  width={3}
                  label="Sq Ft"
                  placeholder="1,800"
                  value={squareFeet}
                  onChange={(e, { value }) => setSquareFeet(value)}
                />
                <SemanticForm.Select
                  width={5}
                  label="Condition"
                  placeholder="Select..."
                  options={CONDITION_OPTIONS}
                  value={condition}
                  onChange={(e, { value }) => setCondition(value)}
                />
              </SemanticForm.Group>
              <SemanticForm.Input
                label="Anything important we should know? (optional)"
                placeholder="e.g. finished basement, easement, upcoming assessment..."
                value={additionalNotes}
                onChange={(e, { value }) => setAdditionalNotes(value)}
              />
            </SemanticForm>
          </>
        )}

        {!addressReady ? (
          <>
            <p style={{ color: GREY_TEXT, textAlign: "center" }}>
              <Icon name="map outline" /> This property doesn't have an address yet. Enter one below to unlock Intelligent Strategies.
            </p>
            <MissingAddressForm
              transactionId={transactionId}
              initialAddress={localPropertyAddress}
              onSaved={handleAddressSaved}
            />
            <div className="clear" />
            <Divider className="medium top margin" />
            <Button onClick={handleClose} type="button" floated={isMobile ? null : "right"} content="Cancel" className={isMobile ? "fluid medium" : null} />
          </>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 8,
                margin: "20px 0 10px",
              }}
            >
              <Header as="h4" style={{ color: NAVY, margin: 0 }}>
                Choose a credit to use:
              </Header>
              <Popup
                position="bottom right"
                trigger={
                  <span style={{ color: BLUE, fontWeight: 600, fontSize: "0.85em", cursor: "pointer" }}>
                    <Icon name="info circle" /> Your Strategy Credit Balances
                  </span>
                }
                content={
                  <div style={{ lineHeight: 1.6 }}>
                    <div>Free / Comped: <b>{freeComped}</b></div>
                    <div>Paid: <b>{paid}</b></div>
                    {manager?.available && (
                      <div>
                        Manager-Sponsored ({manager.managerName || "Manager"}): <b>{manager.balance}</b>
                      </div>
                    )}
                  </div>
                }
              />
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <FundingSourceCard
                icon="gift"
                title="My Free / Comped Credit"
                count={freeComped}
                description="Use one of your complimentary credits."
                selected={fundingSourceType === "free_comped"}
                disabled={freeComped <= 0}
                onSelect={() => setFundingSourceType("free_comped")}
              />
              <FundingSourceCard
                icon="credit card outline"
                title="My Paid Credit"
                count={paid}
                description="Use one of your purchased credits."
                selected={fundingSourceType === "paid"}
                disabled={paid <= 0}
                onSelect={() => setFundingSourceType("paid")}
              />
              <FundingSourceCard
                icon="users"
                title="Manager-Sponsored Credit"
                count={manager?.available ? manager.balance : 0}
                description={manager?.available ? `Provided by ${manager.managerName || "your manager"}.` : "Provided by your manager."}
                selected={fundingSourceType === "manager_pool"}
                disabled={!manager?.available || manager.balance <= 0}
                onSelect={() => setFundingSourceType("manager_pool")}
              />
            </div>

            <Divider hidden />
            <BuyStrategyCreditsSection isMobile={isMobile} transactionId={transactionId} onCreditsFulfilled={handleCreditsFulfilled} />

            <Divider className="medium top margin" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
              <Button basic disabled={isUnlocking} onClick={handleClose} type="button" content="Cancel" className={isMobile ? "fluid medium" : null} />
              <div style={{ textAlign: isMobile ? "left" : "right", width: isMobile ? "100%" : undefined }}>
                <Button
                  primary
                  size="large"
                  fluid={isMobile}
                  loading={isUnlocking}
                  disabled={isUnlocking || !fundingSourceType}
                  onClick={handleConfirm}
                  content="Use 1 Credit & Unlock Strategies"
                />
                <div style={{ color: "#8a92a0", fontSize: "0.75em", marginTop: 4 }}>
                  This unlocks all Intelligent Strategies for this property.
                </div>
              </div>
            </div>
          </>
        )}
      </Segment>
    </ModalWrapper>
  );
}
